import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Lock, Eye, EyeOff, Check, X, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import TwoFactorSection from "@/pages/admin/TwoFactorSection";

const RULES = [
  { id: "len", test: (p) => p.length >= 8, label_fr: "Au moins 8 caractères", label_en: "At least 8 characters" },
  { id: "upper", test: (p) => /[A-Z]/.test(p), label_fr: "Une majuscule", label_en: "One uppercase letter" },
  { id: "lower", test: (p) => /[a-z]/.test(p), label_fr: "Une minuscule", label_en: "One lowercase letter" },
  { id: "digit", test: (p) => /[0-9]/.test(p), label_fr: "Un chiffre", label_en: "One digit" },
];

const AccountPanel = () => {
  const { authedAxios, user, logout } = useAuth();
  const { lang } = useLanguage();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const allRulesPass = RULES.every((r) => r.test(next));
  const matches = next.length > 0 && next === confirm;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit =
    current.length > 0 && allRulesPass && matches && !sameAsCurrent && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const ax = authedAxios();
      await ax.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(
        lang === "fr"
          ? "Mot de passe modifié. Reconnexion requise."
          : "Password changed. Please log in again.",
      );
      setCurrent("");
      setNext("");
      setConfirm("");
      // Force re-login with the new password
      setTimeout(() => {
        logout();
      }, 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="admin-account-panel" className="space-y-12 max-w-2xl">
      <div>
        <p className="overline kw-accent-line">
          {lang === "fr" ? "Compte" : "Account"}
        </p>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-4">
          {lang === "fr" ? "Vos identifiants" : "Your credentials"}
        </h2>
        <p className="mt-3 text-sm text-[#A19E9C]">
          {lang === "fr"
            ? "Modifiez votre mot de passe administrateur. Vous serez déconnecté après la modification."
            : "Change your admin password. You'll be signed out after saving."}
        </p>
      </div>

      {/* Read-only info */}
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="border border-[#F2EBE5]/10 p-5 bg-[#0a0a0a]">
          <p className="kw-label mb-2">{lang === "fr" ? "Email" : "Email"}</p>
          <p className="font-serif-display text-xl text-[#F2EBE5]" data-testid="account-email">
            {user?.email || "—"}
          </p>
        </div>
        <div className="border border-[#F2EBE5]/10 p-5 bg-[#0a0a0a]">
          <p className="kw-label mb-2">{lang === "fr" ? "Rôle" : "Role"}</p>
          <p className="font-serif-display text-xl text-[#C5A059] italic">
            {user?.role || "admin"}
          </p>
        </div>
      </div>

      {/* Change password form */}
      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        onSubmit={handleSubmit}
        data-testid="change-password-form"
        className="space-y-8 border border-[#F2EBE5]/10 p-6 md:p-10 bg-[#0a0a0a]"
      >
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
          <h3 className="font-serif-display text-2xl">
            {lang === "fr" ? "Changer le mot de passe" : "Change password"}
          </h3>
        </div>

        {/* Current password */}
        <div>
          <label htmlFor="current_password" className="kw-label block mb-3">
            {lang === "fr" ? "Mot de passe actuel" : "Current password"} *
          </label>
          <div className="relative">
            <input
              id="current_password"
              type={showCurrent ? "text" : "password"}
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="account-current-password"
              autoComplete="current-password"
              className="kw-input pr-10"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-[#A19E9C] hover:text-[#C5A059] p-2"
              aria-label="toggle"
            >
              {showCurrent ? (
                <EyeOff className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <Eye className="w-4 h-4" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>

        {/* New password */}
        <div>
          <label htmlFor="new_password" className="kw-label block mb-3">
            {lang === "fr" ? "Nouveau mot de passe" : "New password"} *
          </label>
          <div className="relative">
            <input
              id="new_password"
              type={showNext ? "text" : "password"}
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="account-new-password"
              autoComplete="new-password"
              className="kw-input pr-10"
            />
            <button
              type="button"
              onClick={() => setShowNext((v) => !v)}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-[#A19E9C] hover:text-[#C5A059] p-2"
              aria-label="toggle"
            >
              {showNext ? (
                <EyeOff className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <Eye className="w-4 h-4" strokeWidth={1.5} />
              )}
            </button>
          </div>

          {/* Rules checklist */}
          {next.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="password-rules">
              {RULES.map((r) => {
                const ok = r.test(next);
                return (
                  <li
                    key={r.id}
                    className="flex items-center gap-2 text-xs"
                  >
                    {ok ? (
                      <Check className="w-3.5 h-3.5 text-[#C5A059]" strokeWidth={2} />
                    ) : (
                      <X className="w-3.5 h-3.5 text-[#A19E9C]" strokeWidth={2} />
                    )}
                    <span className={ok ? "text-[#F2EBE5]" : "text-[#A19E9C]"}>
                      {lang === "fr" ? r.label_fr : r.label_en}
                    </span>
                  </li>
                );
              })}
              {sameAsCurrent && (
                <li className="flex items-center gap-2 text-xs text-amber-400 pt-2 border-t border-[#F2EBE5]/10 mt-2">
                  <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
                  {lang === "fr"
                    ? "Doit être différent du mot de passe actuel"
                    : "Must differ from the current password"}
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Confirm */}
        <div>
          <label htmlFor="confirm_password" className="kw-label block mb-3">
            {lang === "fr" ? "Confirmer le nouveau mot de passe" : "Confirm new password"} *
          </label>
          <input
            id="confirm_password"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="account-confirm-password"
            autoComplete="new-password"
            className="kw-input"
          />
          {confirm.length > 0 && !matches && (
            <p className="mt-2 text-xs text-red-400">
              {lang === "fr"
                ? "Les mots de passe ne correspondent pas"
                : "Passwords do not match"}
            </p>
          )}
        </div>

        <div className="pt-2 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#A19E9C]">
            {lang === "fr"
              ? "Vous serez déconnecté après modification."
              : "You will be signed out after saving."}
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="account-submit-btn"
            className="kw-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                {lang === "fr" ? "Modification…" : "Saving…"}
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" strokeWidth={1.5} />
                {lang === "fr" ? "Modifier" : "Update"}
              </>
            )}
          </button>
        </div>
      </motion.form>

      <TwoFactorSection />

      <CacheControlSection />
    </section>
  );
};

const CacheControlSection = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [lastVersion, setLastVersion] = useState(null);

  const handleInvalidate = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.post("/admin/cache/invalidate");
      setLastVersion(data.version);
      toast.success(
        lang === "fr"
          ? "Cache visiteurs vidé — vos changements apparaîtront immédiatement"
          : "Visitor cache cleared — your changes will appear instantly",
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      data-testid="cache-control-section"
      className="mt-16 pt-12 border-t border-[#F2EBE5]/8 max-w-2xl"
    >
      <p className="overline text-[#C5A059] mb-3">
        {lang === "fr" ? "Cache visiteurs" : "Visitor cache"}
      </p>
      <h3 className="text-xl md:text-2xl tracking-tight font-serif text-[#F2EBE5] mb-3">
        {lang === "fr" ? "Forcer le rafraîchissement" : "Force a refresh"}
      </h3>
      <p className="text-[13px] leading-relaxed text-[#A19E9C] mb-6">
        {lang === "fr"
          ? "Vos photos sont mises en cache 24h dans le navigateur de chaque visiteur pour un chargement instantané. Si vous venez de changer une couverture et voulez que les visiteurs la voient sans attendre, videz le cache ici. Le cache se rafraîchit automatiquement toutes les 24h."
          : "Photos are cached for 24h in each visitor's browser for instant loads. If you just changed a cover and want visitors to see it without delay, clear the cache here. Cache auto-refreshes every 24h."}
      </p>
      <button
        type="button"
        onClick={handleInvalidate}
        disabled={submitting}
        data-testid="account-cache-invalidate-btn"
        className="kw-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
            {lang === "fr" ? "Vidage…" : "Clearing…"}
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            {lang === "fr" ? "Vider le cache visiteurs" : "Clear visitor cache"}
          </>
        )}
      </button>
      {lastVersion && (
        <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#A19E9C]">
          {lang === "fr" ? "Nouvelle version" : "New version"} #{lastVersion}
        </p>
      )}
    </motion.div>
  );
};

export default AccountPanel;
